import { Button, Callout, Modal, theme } from '@agentisai/design-system';
import { useDeleteWorkstream } from '../queries/useDeleteWorkstream';

const message = {
  margin: 0,
  fontSize: theme.typography.fontSize.body,
  lineHeight: theme.typography.lineHeight.body,
  color: theme.color.neutral.body,
} as const;

const errorSlot = {
  marginTop: theme.space[3],
} as const;

export interface DeleteWorkstreamDialogProps {
  /** The workstream to delete — keys the mutation + its list invalidation. */
  readonly workstreamId: string;
  /** The workstream's title, shown in the confirm copy. */
  readonly title: string;
  /**
   * Dismiss without deleting (Cancel / Escape / backdrop). On this path the
   * triggering card is still in the rail, so the DS `useDialog` cleanup restores
   * focus to its Delete button for free.
   */
  readonly onClose: () => void;
  /**
   * The delete succeeded. Distinct from {@link onClose} because on success the
   * triggering card is about to be removed from the rail — the parent both
   * dismisses the dialog and deselects the workstream (the removed Delete button
   * can't receive focus), instead of relying on `useDialog`'s restore-to-trigger.
   */
  readonly onDeleted: () => void;
}

/**
 * DeleteWorkstreamDialog — the confirm gate for the rail's Delete action. A DS
 * `Modal` (focus-trapped via `useDialog`) asking to hard-delete an entire
 * workstream; on confirm it runs the {@link useDeleteWorkstream} mutation
 * (`DELETE /workstreams/{id}`), which invalidates `listMine` so the deleted card
 * drops from the rail.
 *
 * This is an *irreversible* action that also removes the workstream's chat and
 * every file, so confirm is mandatory and the copy is explicit about the blast
 * radius. Per frontend-architecture R7 the Delete trigger is disabled while the
 * mutation is pending (the double-submit guard — optimistic UI alone would not
 * prevent a double-fire), and the dialog is made non-dismissible mid-flight so
 * Escape/backdrop can't abandon an in-progress delete. A failure keeps the
 * dialog open with an inline error affordance to retry (the BFF leaves the
 * workstream intact on failure, so a retry is safe).
 */
export function DeleteWorkstreamDialog({
  workstreamId,
  title,
  onClose,
  onDeleted,
}: DeleteWorkstreamDialogProps) {
  const mutation = useDeleteWorkstream(workstreamId);
  const pending = mutation.isPending;

  function handleDelete() {
    mutation.mutate(undefined, { onSuccess: onDeleted });
  }

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!pending}
      size="sm"
      title="Delete workstream"
      actions={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={pending}>
            {pending ? 'Deleting…' : 'Delete'}
          </Button>
        </>
      }
    >
      <p style={message}>
        Delete <strong>{title}</strong>? This permanently deletes the workstream, its chat, and all
        its files. This can’t be undone.
      </p>
      {mutation.isError && (
        <div role="alert" style={errorSlot}>
          <Callout tone="danger">Couldn’t delete this workstream. Please try again.</Callout>
        </div>
      )}
    </Modal>
  );
}
DeleteWorkstreamDialog.displayName = 'DeleteWorkstreamDialog';
